import { isRequirementWorkBlocked, normalizeRequirementType } from './requirementType';

export function getRequirementWorkflow(requirement) {
  return requirement?.workflow || {};
}

export function isRequirementClaimed(requirement) {
  const workflow = getRequirementWorkflow(requirement);
  if (workflow.claimStatus) return workflow.claimStatus === 'Claimed';
  return Boolean(requirement?.currentClaimedBy?.userId);
}

export function canClaimRequirement(requirement) {
  return Boolean(getRequirementWorkflow(requirement).canClaim);
}

export function canUnclaimRequirement(requirement) {
  return Boolean(getRequirementWorkflow(requirement).canUnclaim);
}

export function canUploadToRequirement(requirement) {
  return Boolean(getRequirementWorkflow(requirement).canUpload);
}

export function canEditRequirement(requirement, userId = '') {
  const workflow = getRequirementWorkflow(requirement);
  if (typeof workflow.canEdit === 'boolean') return workflow.canEdit;
  if (!userId) return false;
  const createdBy = requirement?.createdBy || requirement?.uploadedBy || '';
  return String(createdBy) === String(userId);
}

export function getUploadDisabledMessage(requirement) {
  return getRequirementWorkflow(requirement).uploadDisabledMessage
    || 'Claim this requirement before uploading profiles or taking candidate actions.';
}

export function getWorkflowBadge(requirement) {
  return getRequirementWorkflow(requirement).workflowBadge || '';
}

export function canAssignRequirement(requirement) {
  return Boolean(getRequirementWorkflow(requirement).canAssign);
}

export function getAssignDisabledMessage(requirement) {
  return getRequirementWorkflow(requirement).assignDisabledMessage
    || 'Claim this requirement before assigning it to your team members.';
}

export function resolveWorkflowBadge(requirement) {
  const badge = getWorkflowBadge(requirement);
  if (badge) return badge;

  const hasClaim = (requirement?.claimedBy || []).length > 0
    || requirement?.claimStatus === 'Claimed'
    || requirement?.currentClaimedBy?.userId;

  if (hasClaim) return 'Claimed';
  if ((requirement?.assignedMembers || []).length > 0) return 'Assigned';
  return 'Pending Claim';
}

export function getWorkflowBadgeClass(badge) {
  switch (badge) {
    case 'Claimed':
      return 'hr-workflow-badge is-claimed';
    case 'Assigned':
      return 'hr-workflow-badge is-assigned';
    case 'Pending Claim':
      return 'hr-workflow-badge is-pending';
    default:
      return 'hr-workflow-badge';
  }
}

export function getCandidateCount(requirement) {
  const workflow = getRequirementWorkflow(requirement);
  if (typeof workflow.candidateCount === 'number') return workflow.candidateCount;
  return requirement?.candidateCount || 0;
}

const STOPPED_REQUIREMENT_TYPES = new Set(['On-Hold-Internal', 'On Hold- Customer', 'Hold']);

/**
 * TL requirement list primary action button label (lifecycle vs claim/upload).
 */
export function getTlRequirementPrimaryActionDisplay(requirement) {
  const type = normalizeRequirementType(requirement?.requirementtype);

  if (type === 'Closed') {
    return { label: 'Closed', blocked: true, tone: 'muted' };
  }
  if (STOPPED_REQUIREMENT_TYPES.has(type)) {
    return { label: 'Stopped', blocked: true, tone: 'muted' };
  }
  if (isRequirementWorkBlocked(requirement?.requirementtype)) {
    return { label: type || 'Blocked', blocked: true, tone: 'muted' };
  }
  if (canClaimRequirement(requirement)) {
    return { label: 'Claim', blocked: false, tone: 'claim' };
  }
  if (canUploadToRequirement(requirement)) {
    return { label: 'Upload', blocked: false, tone: 'upload' };
  }

  const badge = resolveWorkflowBadge(requirement);
  if (badge === 'Pending Claim') {
    return { label: 'Unassigned', blocked: true, tone: 'idle' };
  }
  return { label: 'Assigned', blocked: true, tone: 'assigned' };
}

export function getTlRequirementPrimaryActionButtonColor(tone, { hover = false } = {}) {
  if (tone === 'upload') return hover ? 'darkgreen' : 'MediumSeaGreen';
  if (tone === 'claim') return hover ? 'royalblue' : 'dodgerblue';
  if (tone === 'muted') return hover ? '#6c757d' : '#868e96';
  if (tone === 'idle') return hover ? '#adb5bd' : '#ced4da';
  return hover ? 'darkred' : 'indianred';
}
